import { useRegisterSW } from 'virtual:pwa-register/vue'
import { ref } from 'vue'

const UPDATE_CHECK_INTERVAL =
  import.meta.env.MODE === 'production'
    ? 5 * 60 * 1000 // 5 minutes
    : 2 * 60 * 1000 // dev

const isOfflineReady = ref(false)
const hasUpdateAvailable = ref(false)

let updateSW: ((reload?: boolean) => Promise<void>) | undefined

export function setupServiceWorker() {
  // Safety net: On unload, try to activate waiting service worker if newVersionAvailable is set
  if ('serviceWorker' in navigator) {
    window.addEventListener('beforeunload', () => {
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (
          registration &&
          registration.waiting &&
          localStorage.getItem('newVersionAvailable') === 'true'
        ) {
          try {
            // console.log(
            //   '[SW] [beforeunload] Sending SKIP_WAITING to waiting service worker'
            // )
            registration.waiting.postMessage({ type: 'SKIP_WAITING' })
          } catch (err) {
            console.warn('[SW] Could not send SKIP_WAITING on unload:', err)
          }
        }
      })
    })
  }
  // console.log(
  //   `[SW] Initializing (${import.meta.env.MODE}), interval = ${
  //     UPDATE_CHECK_INTERVAL / 1000
  //   }s`
  // )

  const { offlineReady, needRefresh, updateServiceWorker } = useRegisterSW({
    immediate: true,

    onRegisteredSW(_swUrl, registration) {
      if (!registration) return

      // console.log('[SW] Registered, starting periodic update checks')

      setInterval(async () => {
        try {
          // ❗ Only check for updates when the SW is stable
          if (
            registration.installing ||
            registration.waiting ||
            !registration.active
          ) {
            return
          }

          // console.log('[SW] Checking for updates...')
          await registration.update()
        } catch (err) {
          // Ignore expected lifecycle errors
          if (err instanceof DOMException && err.name === 'InvalidStateError') {
            return
          }

          // console.warn('[SW] Update check failed:', err)
        }
      }, UPDATE_CHECK_INTERVAL)
    },

    onNeedRefresh() {
      //console.log('[SW] 🔄 New version detected')
      localStorage.setItem('newVersionAvailable', 'true')
      hasUpdateAvailable.value = true
    },

    onOfflineReady() {
      // console.log('[SW] ✅ App ready for offline use')
      isOfflineReady.value = true
    },

    onRegisterError(error) {
      console.error('[SW] Registration failed:', error)
    }
  })

  updateSW = updateServiceWorker

  // On app startup, activate waiting service worker if newVersionAvailable is set
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      if (
        localStorage.getItem('newVersionAvailable') === 'true' &&
        registration.waiting
      ) {
        // console.log(
        //   '[SW] Detected waiting service worker and newVersionAvailable flag. Will attempt to send SKIP_WAITING message.'
        // )
        setTimeout(() => {
          if (registration.waiting) {
            // console.log(
            //   '[SW] Sending SKIP_WAITING message to waiting service worker:',
            //   registration.waiting
            // )
            try {
              registration.waiting.postMessage({ type: 'SKIP_WAITING' })
            } catch (err) {
              console.error('[SW] Error sending SKIP_WAITING message:', err)
            }
          } else {
            console.warn(
              '[SW] No waiting service worker found at time of postMessage.'
            )
          }
        }, 500)

        let refreshing = false
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          // console.log('[SW] controllerchange event fired')
          if (refreshing) return
          refreshing = true
          window.location.reload()
        })
      } else {
        if (localStorage.getItem('newVersionAvailable') === 'true') {
          console.warn(
            '[SW] newVersionAvailable is set but no waiting service worker found.'
          )
        }
      }
    })
  }

  // Keep refs in sync
  isOfflineReady.value = offlineReady.value
  hasUpdateAvailable.value = needRefresh.value

  return { isOfflineReady, hasUpdateAvailable }
}

/**
 * Called by the router when a route change is allowed to refresh.
 * This:
 *  - activates the new service worker
 *  - reloads the page under the new version
 */
export function triggerServiceWorkerUpdate(): Promise<void> {
  if (!updateSW) {
    //console.warn('[SW] updateServiceWorker not available')
    return Promise.resolve()
  }

  // console.log('[SW] 🚀 Activating new version')
  localStorage.removeItem('newVersionAvailable')
  hasUpdateAvailable.value = false

  // `true` = skipWaiting + reload once activated
  return updateSW(true)
}

/**
 * Lightweight check for router guards
 */
export function isUpdateAvailable(): boolean {
  return localStorage.getItem('newVersionAvailable') === 'true'
}
